import { Fragment, useState } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

// Use unified date utils
import { moment, formatDateTime } from '../../../utils/dateUtils';

import {
  Autocomplete,
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  Divider,
  Select,
  MenuItem,
  FormControl,
  TableHead,
  TableContainer,
  Paper,
  Checkbox,
  Grid,
  InputLabel,
  Link,
  Modal,
  useMediaQuery,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';

// Icons
import CancelIcon from '@mui/icons-material/Cancel';
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// Custom components
import { ProposalNotes, ProposalTouches, NotesBar, InstallationDate, CheckNumber } from '../../';
import WaitListSection from '../../Proposal/WaitListSection';

// Utils
import { formatPhone } from '../../../utils/formatter';
import messages from '../../../static/messages';

// Services
import proposalsServices from '../../../services/proposalsServices';
import locationServices from '../../../services/locationServices';

// Hooks
import { useLocationAutocomplete } from '../../../hooks/useAutocomplete';

// Styles
import { flex_center, select_no_border } from '../../../static/styles';

const autocomplete_styles = {
  border: 'none',
  '.MuiOutlinedInput-root': {
    padding: 0,
  },
  '.css-md26zr-MuiInputBase-root-MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
    '&::after': {
      content: `''`,
      borderBottom: '2px solid #1976d2 !important',
      left: 0,
      bottom: 0,
      right: 0,
      position: 'absolute !important',
      transform: 'scaleX(0) !important',
      transition: 'transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms !important',
      pointerEvents: 'none !important',
    },
  },
  '.css-md26zr-MuiInputBase-root-MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 0,
    '&::after': {
      transform: 'scaleX(1) translateX(0) !important',
    },
  },
  input: {
    fontSize: '14px !important',
    paddingLeft: '0 !important',
    border: '0 !important',
    '&:focus': {
      border: '0 !important',
    },
  },
  fieldset: {
    border: 0,
  },
  '.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#1976d2',
    borderTop: '0 !important',
    borderRight: '0 !important',
    borderLeft: '0 !important',
    borderRadius: '0 !important',
  },
};

const ProposalDetailsTable = ({ proposal, id, refresh }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const confirm = useConfirm();

  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  const [billingText, setBillingText] = useState('');

  const { handleSearchLocation, locationOptions } = useLocationAutocomplete(2);

  // Handler functions
  const handleChangeStatus = (event) => {
    const newStatus = event.target.value;
    const currentStatus = proposal?.status;
    
    // Special warning when changing from Pending to Active
    if (currentStatus === 'Pending' && newStatus === 'Active') {
      confirm({
        title: '⚠️ Warning: Reverting to Active Status',
        description: 'This action will CLEAR the client signature and "Read" status. The client will need to review and sign the proposal again. Are you sure you want to continue?',
        confirmationText: 'Yes, Clear & Revert',
        cancellationText: 'Cancel',
      })
        .then(() => updateStatus(newStatus))
        .catch(() => toast.info(messages.cancel_message));
    } else {
      confirm({
        title: messages.confirm_message.update,
        description: messages.confirm_message.update_description('status'),
      })
        .then(() => updateStatus(newStatus))
        .catch(() => toast.info(messages.cancel_message));
    }
  };

  const handleChangeAddress = (event, address) => {
    event.preventDefault();
    if (address !== null)
      confirm({
        title: messages.confirm_message.update,
        description: messages.confirm_message.update_description('billing address'),
      })
        .then(() => getLocationInfo(address))
        .catch(() => toast.info(messages.cancel_message));
  };

  const handleChangeType = (event) => {
    confirm({
      title: messages.confirm_message.update,
      description: messages.confirm_message.update_description('type'),
    })
      .then(() => updateDocumentType(event.target.value))
      .catch(() => toast.info(messages.cancel_message));
  };

  const handleSelectClient = (event) => {
    const clientId = event.target.value;
    confirm({
      title: messages.confirm_message.update,
      description: messages.confirm_message.update_description('selected client'),
    })
      .then(() => updateSelectedClient(clientId))
      .catch(() => toast.info(messages.cancel_message));
  };

  // Async functions
  const updateStatus = async (status) => {
    try {
      const response = await proposalsServices.update(id, {
        status,
      });

      if (response.status === 200) {
        toast.success(`Status successfully updated to ${status}`);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const getLocationInfo = async (address) => {
    try {
      const response = await locationServices.getPlace(address);

      if (response.status === 200) updateBillingAddress(response.data);
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const updateBillingAddress = async (address) => {
    try {
      const response = await proposalsServices.update(id, {
        billingPlace: address,
      });
      if (response.status === 200) {
        handleSearchLocation('');
        toast.success(`The billing address was successfully updated`);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Warranty jobs bypass the low-price scheduling guard (when allowed in
  // Settings -> Scheduling)
  const handleToggleWarranty = async (event) => {
    const checked = event.target.checked;
    try {
      const response = await proposalsServices.update(id, { isWarranty: checked });
      if (response.status === 200) {
        toast.success(`Warranty flag ${checked ? 'set' : 'removed'}`);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const updateDocumentType = async (type) => {
    try {
      const response = await proposalsServices.update(id, {
        documentType: type,
      });
      if (response.status === 200) {
        handleSearchLocation('');
        toast.success(`The document type was successfully updated`);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const updateSelectedClient = async (clientId) => {
    try {
      const response = await proposalsServices.update(id, {
        selectedClientId: clientId,
      });

      if (response.status === 200) {
        toast.success(`Client successfully selected`);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const handleOpenBillingDialog = () => {
    setBillingText(proposal?.billingAddress || '');
    setBillingDialogOpen(true);
  };

  const handleSaveBillingAddress = async () => {
    try {
      const response = await proposalsServices.update(id, {
        billingAddress: billingText.trim() || null,
      });
      if (response.status === 200) {
        toast.success('Billing information saved');
        setBillingDialogOpen(false);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const handleRemoveBillingAddress = async () => {
    confirm({
      title: 'Remove Billing Information',
      description: 'Are you sure you want to remove the billing information?',
    })
      .then(async () => {
        try {
          const response = await proposalsServices.update(id, {
            billingAddress: null,
          });
          if (response.status === 200) {
            toast.success('Billing information removed');
            refresh();
          }
        } catch (error) {
          toast.error(messages.errors.error_try_again);
          console.log(error);
        }
      })
      .catch(() => {});
  };

  return (
    <Fragment>
      <Box
        sx={(theme) => ({
          ...flex_center,
          mt: 2,
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
          },
        })}
      >
        <Link href={`/properties/${proposal.property.id}`} underline="hover" color="rgba(0, 0, 0, 0.87)">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {proposal?.property.address}
          </Typography>
        </Link>

        <FormControl
          sx={(theme) => ({
            minWidth: 120,
            maxWidth: 150,
            [theme.breakpoints.down('sm')]: {
              mt: 2,
            },
          })}
          size="small"
        >
          <InputLabel id="status">Status</InputLabel>
          <Select
            labelId="status"
            variant="outlined"
            value={proposal?.status}
            name="status"
            onChange={handleChangeStatus}
            label="Status"
            sx={{
              '& > div': {
                ...flex_center,
              },
            }}
          >
            <MenuItem value="Active">
              <CircleIcon sx={{ color: 'lightgrey', mr: 1, width: 15, height: 15 }} /> Active
            </MenuItem>
            <MenuItem value="Pending">
              <CircleIcon sx={{ color: 'orange', mr: 1, width: 15, height: 15 }} /> Pending
            </MenuItem>
            <MenuItem value="Cold">
              <CircleIcon sx={{ color: '#42a5f5', mr: 1, width: 15, height: 15 }} /> Cold
            </MenuItem>
            <MenuItem value="Completed">
              <CheckCircleIcon sx={{ color: 'green', mr: 1, width: 15, height: 15 }} /> Completed
            </MenuItem>
            <MenuItem value="Cancelled">
              <CancelIcon sx={{ color: 'red', mr: 1, width: 15, height: 15 }} /> Cancelled
            </MenuItem>
            <MenuItem value="Lost">
              <CircleIcon sx={{ color: '#e91e63', mr: 1, width: 15, height: 15 }} /> Lost
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Typography sx={{ fontWeight: 600, mb: 1, color: '#2e7d32' }}>Proposal Info {proposal.publicId}:</Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableBody>
                {/* BILLING ADDRESS - Hidden (rarely used, confuses users)
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: '50%' }}>Billing Address</TableCell>
                  <TableCell>
                    <Autocomplete
                      options={locationOptions}
                      getOptionLabel={(option) => option}
                      filterOptions={(x) => x}
                      onChange={handleChangeAddress}
                      value={proposal?.billingPlace?.label}
                      freeSolo
                      sx={autocomplete_styles}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="billingPlace"
                          onChange={(e) => handleSearchLocation(e.target.value)}
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
                */}

                {/* INSTALLATION DATE */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: '50%' }}>Installation Date</TableCell>
                  <TableCell>
                    <InstallationDate data={proposal?.installationSchedule} id={id} refresh={refresh} isSigned={!!(proposal?.signatureId || proposal?.signature)} proposal={proposal} />
                  </TableCell>
                </TableRow>

                {/* WAIT LIST / FIXES */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: '50%' }}>Wait List / Fixes</TableCell>
                  <TableCell>
                    <WaitListSection proposal={proposal} id={id} refresh={refresh} />
                  </TableCell>
                </TableRow>

                {/* DOCUMENT TYPE */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: '50%' }}>Document Type</TableCell>
                  <TableCell>
                    <Select
                      name="documentType"
                      value={proposal?.documentType || 'Proposal'}
                      size="small"
                      onChange={handleChangeType}
                      sx={select_no_border}
                    >
                      <MenuItem value="Proposal">Proposal</MenuItem>
                      <MenuItem value="Contract">Contract</MenuItem>
                      <MenuItem value="Invoice">Invoice</MenuItem>
                      <MenuItem value="Receipt">Receipt</MenuItem>
                      <MenuItem value="Amount Due">Amount Due</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>

                {/* WARRANTY */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: '50%' }}>Warranty</TableCell>
                  <TableCell>
                    <Checkbox
                      checked={Boolean(proposal?.isWarranty)}
                      onChange={handleToggleWarranty}
                      size="small"
                      sx={{ p: 0.5 }}
                    />
                  </TableCell>
                </TableRow>

                {/* RECEIPT NUMBER */}
                {proposal?.documentType === 'Receipt' && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '50%' }}>Receipt check number</TableCell>
                    <TableCell>
                      <CheckNumber data={proposal} id={id} refresh={refresh} />
                    </TableCell>
                  </TableRow>
                )}

                {/* CREATED BY */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: '50%' }}>Created By</TableCell>
                  <TableCell>
                    {proposal?.createdBy?.fullName || 'Unknown'} ({formatDateTime(proposal?.createdAt, 'L, h:mm:ss a')})
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          {/* Billing Address */}
          {proposal?.billingAddress ? (
            <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: isMobile ? '0.85rem' : '0.9rem', color: '#555' }}>
                  Billing Information
                </Typography>
                <Box>
                  <IconButton size="small" onClick={handleOpenBillingDialog} title="Edit">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={handleRemoveBillingAddress} title="Remove" color="error">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#333' }}>
                {proposal.billingAddress}
              </Typography>
            </Paper>
          ) : (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenBillingDialog}
              sx={{ mb: 1, textTransform: 'none' }}
            >
              Add billing address
            </Button>
          )}

          <Typography sx={{ fontWeight: 600, mb: 1, fontSize: isMobile ? '0.9rem' : 'inherit' }}>
            Select Client for Proposal:
          </Typography>

          <Paper sx={{ p: 2 }}>
            {proposal?.property?.clients?.length ? (
              <FormControl fullWidth size="small">
                <InputLabel id="select-client-label">Client</InputLabel>
                <Select
                  labelId="select-client-label"
                  value={proposal?.selectedClientId || ''}
                  onChange={handleSelectClient}
                  label="Client"
                >
                  {proposal?.property?.clients?.map((client) => {
                    if (!client?.client) return null;
                    return (
                      <MenuItem key={client.client.id} value={client.client.id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography sx={{ fontWeight: 500 }}>
                            {client.client.fullName}
                            {client.relation && (
                              <Typography component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.85rem' }}>
                                ({client.relation})
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {formatPhone(client.client.phoneNumber)} • {client.client.email}
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            ) : (
              <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
                No clients linked to this property
              </Typography>
            )}
            
            {/* Selected client details */}
            {proposal?.selectedClientId && proposal?.property?.clients?.length > 0 && (
              <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                {(() => {
                  const selectedClient = proposal.property.clients.find(
                    (c) => c.client?.id === proposal.selectedClientId
                  )?.client;
                  if (!selectedClient) return null;
                  return (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        <Link href={`/clients/${selectedClient.id}`} underline="hover" color="primary">
                          {selectedClient.fullName}
                        </Link>
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                        📞 {formatPhone(selectedClient.phoneNumber)}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                        ✉️ {selectedClient.email}
                      </Typography>
                    </>
                  );
                })()}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Box>
            <ProposalNotes notesList={proposal.notesForInstallers} id={id} refresh={refresh} />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Property Notes:</Typography>

            {proposal.property.notes.map((el) => (
              <Fragment key={el.id}>
                <Box alignItems="center" sx={{ display: 'flex', mt: 1 }} key={el.id}>
                  <Typography variant="body1">
                    {el.note}
                    {` `}
                    <Typography variant="caption" sx={{ fontStyle: 'italic ' }}>
                      (Created by <strong>{el.createdBy?.fullName || 'Unknown'}</strong> at{' '}
                      {formatDateTime(el.createdAt, 'L, h:mm:ss a')})
                    </Typography>
                  </Typography>
                </Box>
                <Divider sx={{ mt: 1 }} />
              </Fragment>
            ))}
          </Box>
        </Grid>
      </Grid>

      {/* Contact History (Touches) */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
            <ProposalTouches proposalId={id} contactHistoryColor={proposal?.contactHistoryColor} />
          </Box>
        </Grid>
      </Grid>

      <NotesBar
        orders={proposal?.equipmentOrders}
        data={proposal?.additionalNotes}
        id={id}
        publicId={proposal?.publicId}
        refresh={refresh}
        systems={proposal?.proposalSystems}
        installationSchedule={proposal?.installationSchedule}
        createdBy={proposal?.createdBy}
        proposal={proposal}
      />

      {/* Billing Address Dialog */}
      <Dialog
        open={billingDialogOpen}
        onClose={() => setBillingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {proposal?.billingAddress ? 'Edit Billing Information' : 'Add Billing Information'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            minRows={4}
            maxRows={10}
            fullWidth
            placeholder={"Company Name\n123 Main St, Suite 100\nSan Diego, CA 92101\nAttn: Accounts Payable"}
            value={billingText}
            onChange={(e) => setBillingText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBillingDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveBillingAddress} variant="contained" disabled={!billingText.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default ProposalDetailsTable;
